import { Button } from "@/components/ui/buttons/Button";
import { IconButton } from "@/components/ui/buttons/IconButton";
import { Icon } from "@/components/ui/display/Icon";
import { ConfirmDialog } from "@/components/ui/feedback/ConfirmDialog";
import { FlashAlert } from "@/components/ui/feedback/FlashAlert";
import { StatusMessages } from "@/components/ui/feedback/StatusMessages";
import { Tooltip } from "@/components/ui/feedback/Tooltip";
import { Select } from "@/components/ui/forms/Select";
import { FilteredList } from "@/components/ui/lists/FilteredList";
import { UserListItem } from "@/components/ui/lists/UserListItem";
import type { SelectOption } from "@/components/ui/forms/Select/types";
import { useSuperusers, useUsers } from "@/hooks/data";
import { deriveUserNames } from "@/hooks/data/dataQuality";
import type { ISuperuser, IUser } from "@/services";
import classNames from "classnames";
import React, { useCallback, useMemo, useState } from "react";
import layoutStyles from "@/components/admin/AdminSectionLayout.module.css";
import styles from "./SuperusersSection.module.css";
import type { SuperusersSectionProps } from "./types";
import SectionHeader from "@/components/layout/SectionHeader";

/** Explanation text for superuser role */
const SUPERUSER_EXPLANATION =
  "En superbruker kan opprette, endre og slette andres bookinger, opprette nye kunder og administrere soft bookinger for kunder";

/**
 * SuperusersSection - Manage superuser assignments
 * Part of the Admin page
 */
export const SuperusersSection: React.FC<SuperusersSectionProps> = ({
  className,
  "data-testid": testId = "superusers-section",
}) => {
  const {
    sortedSuperusers,
    isLoading: superusersLoading,
    error: superusersError,
    createSuperuser,
    deleteSuperuser,
    isCreating,
    isDeleting,
    findByUserId,
  } = useSuperusers();

  const { sortedUsers, isLoading: usersLoading } = useUsers();

  // State for add form
  const [selectedUser, setSelectedUser] = useState<SelectOption<IUser>[]>([]);

  // State for delete confirmation
  const [deleteTarget, setDeleteTarget] = useState<ISuperuser | null>(null);

  // State for flash alerts
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  // State for mobile help tooltip
  const [showHelpTooltip, setShowHelpTooltip] = useState(false);

  // Filter out users who are already superusers
  const availableUsers = useMemo(() => {
    return sortedUsers.filter((user) => !findByUserId(user.id));
  }, [sortedUsers, findByUserId]);

  // Convert users to select options
  // Label includes both name and email for search; renderOption shows styled display
  const userOptions: SelectOption<IUser>[] = useMemo(() => {
    return availableUsers.map((user) => {
      const { displayName } = deriveUserNames(user);
      return {
        id: user.id,
        // Include email in label for search matching
        label: `${displayName} ${user.email}`,
        data: user,
      };
    });
  }, [availableUsers]);

  const renderUserOption = useCallback((option: SelectOption<IUser>) => {
    const { displayName } = option.data
      ? deriveUserNames(option.data)
      : { displayName: option.label };
    return (
      <div className={styles.userSelectCard}>
        <div className={styles.userSelectName}>{displayName}</div>
        <div className={styles.userSelectEmail}>
          {option.data?.email ?? option.label}
        </div>
      </div>
    );
  }, []);

  // Custom renderer for selected value display in input
  const renderUserValue = useCallback((option: SelectOption<IUser>) => {
    const { displayName } = option.data
      ? deriveUserNames(option.data)
      : { displayName: option.label };
    return (
      <div className={styles.userSelectValue}>
        <span className={styles.userSelectValueName}>{displayName}</span>
        <span className={styles.userSelectValueEmail}>
          {option.data?.email}
        </span>
      </div>
    );
  }, []);

  // Handle add superuser
  const handleAdd = useCallback(async () => {
    if (selectedUser.length === 0 || !selectedUser[0].data) return;

    const email = selectedUser[0].data.email;
    const result = await createSuperuser({ email });

    if (result) {
      setSuccessMessage("Superbruker lagt til");
      setSelectedUser([]);
    } else {
      setErrorMessage("Kunne ikke legge til superbruker");
    }
  }, [selectedUser, createSuperuser]);

  // Handle delete confirmation
  const handleDeleteClick = useCallback((superuser: ISuperuser) => {
    setDeleteTarget(superuser);
  }, []);

  // Handle delete confirm
  const handleDeleteConfirm = useCallback(async () => {
    if (!deleteTarget) return;

    const success = await deleteSuperuser(deleteTarget.email);

    if (success) {
      setSuccessMessage("Superbruker fjernet");
    } else {
      setErrorMessage("Kunne ikke fjerne superbruker");
    }

    setDeleteTarget(null);
  }, [deleteTarget, deleteSuperuser]);

  // Handle delete cancel
  const handleDeleteCancel = useCallback(() => {
    setDeleteTarget(null);
  }, []);

  const isLoading = superusersLoading || usersLoading;

  return (
    <div
      className={classNames(layoutStyles.section, styles.section, className)}
      data-testid={testId}
    >
      {/* Explanation text - visible only on desktop */}
      <p className={layoutStyles.explanationText}>{SUPERUSER_EXPLANATION}</p>

      {/* Flash alerts */}
      {successMessage && (
        <FlashAlert
          alert={{ message: successMessage, variant: "success" }}
          onDismiss={() => setSuccessMessage(null)}
          data-testid={`${testId}-success-alert`}
        />
      )}
      {errorMessage && (
        <FlashAlert
          alert={{ message: errorMessage, variant: "error" }}
          onDismiss={() => setErrorMessage(null)}
          data-testid={`${testId}-error-alert`}
        />
      )}

      {/* Add superuser form */}
      <div className={layoutStyles.dataAreaGrid}>
        <section className={layoutStyles.card} data-area="superuser-search">
          <SectionHeader
            as="h2"
            size="md"
            action={
              <div className={styles.helpButtonWrapper}>
                <Tooltip
                  content={SUPERUSER_EXPLANATION}
                  position="left"
                  width="md"
                  show={showHelpTooltip}
                >
                  <IconButton
                    icon={<Icon name="help-circle" size="sm" aria-hidden />}
                    variant="default"
                    size="sm"
                    onClick={() => setShowHelpTooltip((prev) => !prev)}
                    aria-label="Hva er en superbruker?"
                    className={styles.helpButton}
                    data-testid={`${testId}-help-button`}
                  />
                </Tooltip>
              </div>
            }
          >
            Legg til superbruker
          </SectionHeader>

          <StatusMessages
            error={superusersError}
            isLoading={isLoading}
            errorLabel="Kunne ikke laste superbrukere"
            loadingLabel="Laster superbrukere..."
            testId={testId}
          />

          {!isLoading && !superusersError && (
            <div className={styles.addForm} data-testid={`${testId}-add-form`}>
              <div className={styles.formField}>
                <label htmlFor="superuser-select" className={styles.fieldLabel}>
                  Velg bruker
                </label>
                <div className={styles.selectWrapper}>
                  <Select<IUser>
                    id="superuser-select"
                    appearance="clean"
                    placeholder="Søk etter navn eller e-post..."
                    size="md"
                    options={userOptions}
                    value={selectedUser}
                    onChange={setSelectedUser}
                    searchable
                    disabled={isCreating}
                    data-testid={`${testId}-user-select`}
                    renderOption={renderUserOption}
                    renderValue={renderUserValue}
                  />
                </div>
                <span className={styles.fieldHelp}>
                  Velg en bruker fra listen som skal få superbruker-tilgang
                </span>
              </div>

              <div className={styles.buttonRow}>
                <Button
                  onClick={handleAdd}
                  disabled={selectedUser.length === 0}
                  isLoading={isCreating}
                  loadingText="Legger til..."
                  className={styles.addButton}
                  data-testid={`${testId}-add-button`}
                >
                  Legg til superbruker
                </Button>
              </div>
            </div>
          )}
        </section>
        <section className={layoutStyles.card} data-area="superuser-list">
          {/* Superusers list */}
          {!isLoading && !superusersError && (
            <>
              <SectionHeader as="h2" size="md">
                Liste over superbrukere
              </SectionHeader>
              <FilteredList
                items={sortedSuperusers}
                filterItem={(superuser, normalized) => {
                  const searchFields = [
                    superuser.first_name,
                    superuser.last_name,
                    superuser.email,
                    superuser.name,
                  ];
                  return searchFields
                    .filter(Boolean)
                    .some((value) => value!.toLowerCase().includes(normalized));
                }}
                renderItem={(superuser) => (
                  <UserListItem
                    key={superuser.id}
                    user={superuser}
                    onDelete={handleDeleteClick}
                    isDeleting={isDeleting}
                    deleteLabel="Fjern"
                    className={styles.listItem}
                    data-testid={`${testId}-item-${superuser.id}`}
                  />
                )}
                placeholder="Søk etter navn eller e-post"
                debounce={150}
                searchDisabled={isLoading}
                maxItems={6}
                itemHeight={58}
                className={styles.listWrapper}
                data-testid={`${testId}-filtered-list`}
              />
            </>
          )}
        </section>
      </div>

      {/* Delete confirmation dialog */}
      <ConfirmDialog
        isOpen={deleteTarget !== null}
        title="Fjern superbruker"
        text={`Er du sikker på at du vil fjerne ${deleteTarget?.name} som superbruker?`}
        confirmText="Fjern"
        cancelText="Avbryt"
        destructive
        onConfirm={handleDeleteConfirm}
        onCancel={handleDeleteCancel}
        data-testid={`${testId}-delete-dialog`}
      />
    </div>
  );
};

SuperusersSection.displayName = "SuperusersSection";

export default SuperusersSection;
